// /translate/mfa — MFA / Consular legalization hub (TH)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute } from "@tanstack/react-router";
import { CertTypeHub } from "@/components/translate/CertTypeHub";
import { getCertType } from "@/data/translation-certtypes";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";
const cert = getCertType("mfa")!;

export const Route = createFileRoute("/translate/mfa")({
  head: () => {
    const url = `${SITE}/translate/mfa`;
    const title = `${cert.nameTh} | NYC Language Institute`;
    const desc = cert.taglineTh;
    return {
      meta: [
        { title: ctaTitle(title, "/translate/mfa", "th") }, { name: "description", content: ctaDesc(desc, "/translate/mfa", "th")},
        { name: "keywords", content: cert.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/translate/mfa", "th")}, { property: "og:description", content: ctaDesc(desc, "/translate/mfa", "th")},
        { property: "og:url", content: url },
      ],
      links: canonicalAndHreflang("/translate/mfa", "/en/translate/mfa"),
      scripts: [
        speakableSchema(),
        howToSchema({
          name: "ขั้นตอนแปล + รับรอง MFA กับ NYC",
          description: cert.taglineTh,
          totalTime: "PT5D", estimatedCost: { currency: "THB", value: String(cert.priceFromThb) },
          steps: cert.stepsTh.map((s) => ({ name: s.name, text: s.text })),
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/translate/mfa", serviceType: "MFA Consular Legalization", name: title, description: desc, priceFromThb: cert.priceFromThb }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "FAQPage", "@id": `${url}#faq`,
              mainEntity: cert.faqsTh.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "แปลรายภาษา", item: `${SITE}/translate` },
                { "@type": "ListItem", position: 3, name: "MFA", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({
          url: `${SITE}/api/public/mcp`,
          name: "NYC Language Institute — MCP Server",
          description: "AI agent endpoint exposing MFA / consular legalization tooling.",
        }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      ],
    };
  },
  component: () => <CertTypeHub cert={cert} locale="th" />,
});
